import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { SEO } from "@/components/SEO";
import { DownloadApp } from "@/components/DownloadApp";
import { motion } from "framer-motion";
import api from "@/lib/api";
import {
  ArrowUpRight,
  ScanLine,
  Sparkles,
  TrendingUp,
  ShieldCheck,
  Zap,
  Check,
  CheckCircle2,
  Mail,
  Truck,
  Users,
  FileWarning,
  Bell,
  Home,
  Landmark,
  BookOpen,
  Crown,
} from "lucide-react";

const stats = [
  { label: "Avg items removed", value: "7.2", suffix: "/user" },
  { label: "Est. score lift", value: "+84", suffix: "pts" },
  { label: "Bureaus covered", value: "3", suffix: "of 3" },
];

const steps = [
  {
    num: "01",
    icon: ScanLine,
    title: "Feed it your report.",
    body: "Drop a PDF or paste the text. Claude 4.5 tears through it and pulls every derogatory account, inquiry, and dirty date.",
  },
  {
    num: "02",
    icon: Sparkles,
    title: "AI writes your letters.",
    body: "Six proven strategies. FCRA-cited demands. Zero legalese to google. Ready-to-mail in under 30 seconds.",
  },
  {
    num: "03",
    icon: TrendingUp,
    title: "Watch things vanish.",
    body: "Track every dispute from draft → sent → resolved. Your credit history stops being a horror story.",
  },
];

// Features-announce grid: icon + short name only. Curiosity is the point.
const featureTiles = [
  { icon: ScanLine, name: "AI Report Scan" },
  { icon: Sparkles, name: "Letter Engine" },
  { icon: Mail, name: "Certified Mail" },
  { icon: TrendingUp, name: "Score Tracker" },
  { icon: Home, name: "Mortgage Sim" },
  { icon: FileWarning, name: "CFPB Escalation" },
  { icon: ShieldCheck, name: "ID-Theft Kit" },
  { icon: Bell, name: "Smart Reminders" },
  { icon: Landmark, name: "Tri-Bureau Split" },
  { icon: BookOpen, name: "Credit Playbook" },
  { icon: Crown, name: "Concierge Review" },
];

const brands = [
  "Equifax", "Experian", "TransUnion", "FCRA §1681", "CROA",
  "Equifax", "Experian", "TransUnion", "FCRA §1681", "CROA",
];

export default function Landing() {
  const [pricing, setPricing] = useState(null);
  useEffect(() => {
    let ignore = false;
    api
      .get("/pricing")
      .then(({ data }) => {
        if (!ignore) setPricing(data.prices || []);
      })
      .catch(() => {});
    return () => {
      ignore = true;
    };
  }, []);
  const basicPrice = pricing?.find((p) => p.lookup_key === "aura_basic_monthly");
  const plusPrice = pricing?.find((p) => p.lookup_key === "aura_plus_monthly");
  const priceStr = (p) => (p ? (p.price_cents / 100).toFixed(2).replace(/\.00$/, "") : null);
  return (
    <div className="bg-[#0B0C0E] text-[#F5F3EE] min-h-screen">
      <SEO
        title="AI Credit Repair — remove bad items with real USPS Certified Mail"
        description="Aura Credit is the modern AI credit-repair app. Upload your report, get bureau-grade FCRA dispute letters drafted in seconds, and mail them via real USPS Certified Mail — all in one place. Monthly membership, cancel anytime. No hidden fees."
        path="/"
        keywords="AI credit repair, credit repair app, remove collections, credit dispute letters, USPS certified mail, FCRA §1681i, boost credit score"
      />
      {/* Nav */}
      <header className="border-b border-[#26282C]/60 bg-[#0B0C0E]/85 backdrop-blur-md sticky top-0 z-30">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5" data-testid="brand-link">
            <div className="w-7 h-7 rounded-sm bg-[#D5FF3F] flex items-center justify-center">
              <span className="font-display italic text-[#0B0C0E] text-lg leading-none">a</span>
            </div>
            <span className="font-display text-2xl">Aura</span>
            <span className="text-xs text-[#6D716C] font-mono uppercase tracking-widest ml-1">/credit</span>
          </Link>
          <nav className="hidden md:flex items-center gap-8 text-sm text-[#A8ADA5]">
            <a href="#how" className="hover:text-[#F5F3EE] transition-colors">How</a>
            <a href="#stack" className="hover:text-[#F5F3EE] transition-colors">Stack</a>
            <a href="#pricing" className="hover:text-[#F5F3EE] transition-colors">Pricing</a>
            <a href="#truth" className="hover:text-[#F5F3EE] transition-colors">Truth</a>
          </nav>
          <div className="flex items-center gap-2">
            <Link to="/login" data-testid="header-login-link">
              <Button variant="ghost" className="rounded-full text-[#A8ADA5] hover:text-[#F5F3EE] hover:bg-[#17191C]">
                Sign in
              </Button>
            </Link>
            <Link to="/register" data-testid="header-register-link">
              <Button className="rounded-full btn-lime px-5 h-9 text-sm font-medium">
                Start free <ArrowUpRight size={14} className="ml-1" />
              </Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="grid-bg absolute inset-0 opacity-40" />
        <div className="absolute -top-40 -right-40 w-[500px] h-[500px] rounded-full bg-[#D5FF3F]/10 blur-[120px] pointer-events-none" />
        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10 py-20 lg:py-28">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
            className="flex items-center gap-2 mb-10"
          >
            <div className="inline-flex items-center gap-2 border border-[#26282C] bg-[#131518]/60 backdrop-blur rounded-full px-3 py-1.5">
              <span className="w-1.5 h-1.5 rounded-full bg-[#D5FF3F] animate-pulse" />
              <span className="text-xs text-[#A8ADA5] font-mono uppercase tracking-wider">
                Powered by Claude 4.5 · Live
              </span>
            </div>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
            className="max-w-5xl text-[13vw] md:text-[9vw] lg:text-[7.2rem] xl:text-[8.5rem] leading-[0.92] tracking-[-0.03em] font-medium"
          >
            Delete the bad
            <br />
            <span className="font-display text-[#D5FF3F]">credit.</span>
            <span className="text-[#6D716C]"> Keep the</span>
            <br />
            <span className="font-display">good life.</span>
          </motion.h1>

          {/* Big/bold PWA install CTA — the primary above-the-fold action */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="mt-10 md:mt-14 flex flex-col items-start gap-3"
          >
            <DownloadApp size="xl" variant="hero" data-testid="hero-download-app" />
            <div className="flex items-center gap-2 text-[11px] font-mono uppercase tracking-widest text-[#6D716C] pl-2">
              <span className="w-1.5 h-1.5 rounded-full bg-[#7CFF9B] animate-pulse" />
              Works on iPhone, Android &amp; desktop · No app store needed
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.25, ease: [0.22, 1, 0.36, 1] }}
            className="mt-12 grid lg:grid-cols-12 gap-10 items-end"
          >
            <div className="lg:col-span-6">
              <p className="text-lg text-[#A8ADA5] leading-relaxed max-w-xl">
                Aura is an AI credit engineer. It reads your report, spots every disputable
                mark, and drafts bureau-grade letters that actually get things removed —
                in the time it takes to make coffee.
              </p>
              <div className="mt-10 flex flex-wrap items-center gap-3">
                <Link to="/register" data-testid="hero-cta-primary">
                  <Button className="rounded-full btn-lime h-12 px-7 text-base font-medium">
                    Run free scan <ArrowUpRight size={16} className="ml-1.5" />
                  </Button>
                </Link>
                <Link to="/login" data-testid="hero-cta-secondary">
                  <Button variant="ghost" className="rounded-full h-12 px-7 btn-outline text-base">
                    Sign in
                  </Button>
                </Link>
              </div>
              <div className="mt-8 flex items-center gap-6 text-xs text-[#6D716C] font-mono uppercase tracking-wider">
                <div className="flex items-center gap-2">
                  <ShieldCheck size={13} /> Zero-knowledge storage
                </div>
                <div className="flex items-center gap-2">
                  <Zap size={13} /> 30-second scan
                </div>
              </div>
            </div>

            {/* Live scan card */}
            <div className="lg:col-span-6 lg:col-start-7">
              <div className="card-surface card-elevate relative overflow-hidden">
                <div className="flex items-center justify-between px-5 py-3 border-b border-[#26282C]">
                  <div className="flex items-center gap-2">
                    <div className="flex gap-1.5">
                      <div className="w-2.5 h-2.5 rounded-full bg-[#FF6B4A]/60" />
                      <div className="w-2.5 h-2.5 rounded-full bg-[#D5FF3F]/60" />
                      <div className="w-2.5 h-2.5 rounded-full bg-[#7CFF9B]/60" />
                    </div>
                    <span className="text-xs text-[#6D716C] font-mono uppercase tracking-widest ml-3">
                      aura://scan/live
                    </span>
                  </div>
                  <span className="text-xs text-[#D5FF3F] font-mono">● 200 OK</span>
                </div>
                <div className="p-6 space-y-5">
                  <div>
                    <div className="overline mb-2">Result</div>
                    <div className="flex items-baseline gap-3">
                      <span className="ticker text-6xl text-[#D5FF3F]">7</span>
                      <span className="text-lg text-[#A8ADA5]">items flagged</span>
                    </div>
                    <div className="text-sm text-[#6D716C] mt-1">Est. score lift · <span className="text-[#7CFF9B]">+84 pts</span></div>
                  </div>
                  <div className="border-t border-[#26282C] pt-4 space-y-3">
                    {[
                      { c: "Midland Credit Mgmt", t: "Collection · $842", s: "high" },
                      { c: "Capital One", t: "Late 90d · $0", s: "medium" },
                      { c: "LVNV Funding", t: "Charge-off · $3,204", s: "high" },
                    ].map((row, i) => (
                      <div key={i} className="flex items-center justify-between text-sm">
                        <div>
                          <div className="text-[#F5F3EE]">{row.c}</div>
                          <div className="text-xs text-[#6D716C] font-mono">{row.t}</div>
                        </div>
                        <span className={`text-[10px] font-mono uppercase tracking-widest px-2 py-1 rounded-full ${
                          row.s === "high" ? "bg-[#FF6B4A]/15 text-[#FF6B4A]" : "bg-[#D5FF3F]/15 text-[#D5FF3F]"
                        }`}>
                          {row.s}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Stats bar */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.7, staggerChildren: 0.05, delayChildren: 0.1 }}
            className="mt-24 grid grid-cols-1 md:grid-cols-3 gap-px bg-[#26282C] border border-white/5"
          >
            {stats.map((s, i) => (
              <motion.div
                key={s.label}
                initial={{ opacity: 0, y: 8 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.05 * i }}
                className="bg-[#0B0C0E] p-6 hover:bg-[#131518] transition-colors duration-300"
              >
                <div className="overline mb-3">{s.label}</div>
                <div className="flex items-baseline gap-1.5">
                  <span className="ticker text-4xl text-[#F5F3EE]">{s.value}</span>
                  <span className="text-sm text-[#6D716C]">{s.suffix}</span>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Marquee bar */}
      <section className="border-y border-[#26282C] overflow-hidden bg-[#131518]">
        <div className="py-4 marquee">
          {brands.map((b, i) => (
            <div key={i} className="flex items-center gap-8 text-[#6D716C] text-sm font-mono uppercase tracking-widest whitespace-nowrap">
              <span>{b}</span>
              <span className="text-[#D5FF3F]">◆</span>
            </div>
          ))}
        </div>
      </section>

      {/* Tracking-number advertisement — proof this is a real service, not a demo */}
      <section
        id="tracking"
        className="border-b border-[#26282C] bg-gradient-to-br from-[#7CFF9B]/6 to-transparent"
        data-testid="landing-tracking-callout"
      >
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20 md:py-24 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <div className="inline-flex items-center gap-2 border border-[#7CFF9B]/40 bg-[#7CFF9B]/10 rounded-full px-3 py-1.5 mb-6">
              <Truck size={13} className="text-[#7CFF9B]" />
              <span className="text-xs font-mono uppercase tracking-widest text-[#7CFF9B]">
                Real USPS tracking · every letter
              </span>
            </div>
            <h2 className="text-4xl md:text-5xl leading-[0.95] tracking-tight mb-5">
              We don&rsquo;t just draft letters.
              <br />
              <span className="font-display text-[#7CFF9B]">We mail them.</span>
              {" "}And prove it.
            </h2>
            <p className="text-[#A8ADA5] text-lg max-w-2xl mb-8 leading-relaxed">
              Every dispute you pay to send goes out as <span className="text-[#F5F3EE]">USPS Certified
              Mail with Return Receipt</span>. You get a real tracking number the moment we hand it to
              USPS &mdash; no screenshots, no fake proof-of-mailing. Look it up on usps.com and watch it
              hit the bureau.
            </p>
            <div className="flex flex-wrap items-center gap-3">
              <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-[#131518] px-4 py-2 text-sm">
                <CheckCircle2 size={14} className="text-[#7CFF9B]" />
                <span className="text-[#F5F3EE]">Tracking on every send</span>
              </div>
              <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-[#131518] px-4 py-2 text-sm">
                <CheckCircle2 size={14} className="text-[#7CFF9B]" />
                <span className="text-[#F5F3EE]">Return receipt confirmation</span>
              </div>
              <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-[#131518] px-4 py-2 text-sm">
                <CheckCircle2 size={14} className="text-[#7CFF9B]" />
                <span className="text-[#F5F3EE]">$12 flat per letter · no quotas</span>
              </div>
            </div>
          </div>

          {/* USPS-style tracking mockup */}
          <div className="lg:col-span-5">
            <div className="card-surface p-6 shadow-2xl relative overflow-hidden">
              <div className="absolute -top-16 -right-16 w-56 h-56 rounded-full bg-[#7CFF9B]/12 blur-3xl pointer-events-none" />
              <div className="relative">
                <div className="flex items-center justify-between mb-4">
                  <div className="overline text-[#7CFF9B]">USPS · certified mail</div>
                  <span className="text-[10px] px-2 py-0.5 rounded-full font-mono uppercase tracking-widest bg-[#7CFF9B] text-[#0B0C0E]">
                    Delivered
                  </span>
                </div>
                <div className="ticker text-2xl md:text-3xl text-[#F5F3EE] tracking-wider mb-1 break-all">
                  9414 7266 9904 2001 5478 21
                </div>
                <div className="text-xs font-mono text-[#6D716C] mb-6">
                  Tracking # · yours the moment we mail
                </div>
                <div className="space-y-3 text-sm">
                  <div className="flex items-start gap-2">
                    <CheckCircle2 size={14} className="text-[#7CFF9B] mt-0.5 flex-shrink-0" strokeWidth={2.5} />
                    <div>
                      <div className="text-[#F5F3EE]">Delivered</div>
                      <div className="text-xs text-[#6D716C]">Atlanta, GA 30374 · Equifax mail room</div>
                    </div>
                  </div>
                  <div className="flex items-start gap-2">
                    <Truck size={14} className="text-[#A8ADA5] mt-0.5 flex-shrink-0" />
                    <div>
                      <div className="text-[#F5F3EE]">Out for delivery</div>
                      <div className="text-xs text-[#6D716C]">USPS processing facility</div>
                    </div>
                  </div>
                  <div className="flex items-start gap-2">
                    <Mail size={14} className="text-[#A8ADA5] mt-0.5 flex-shrink-0" />
                    <div>
                      <div className="text-[#F5F3EE]">Mailed by Aura Credit</div>
                      <div className="text-xs text-[#6D716C]">Accepted · USPS Certified w/ Return Receipt</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section id="how" className="relative border-b border-[#26282C]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-28">
          <div className="grid lg:grid-cols-12 gap-8 mb-20">
            <div className="lg:col-span-4">
              <div className="overline mb-4">The stack</div>
              <h2 className="text-5xl lg:text-6xl leading-[0.95] tracking-tight">
                Three moves.
                <br />
                <span className="font-display text-[#D5FF3F]">Zero</span> filler.
              </h2>
            </div>
            <div className="lg:col-span-7 lg:col-start-6 flex items-end">
              <p className="text-lg text-[#A8ADA5] leading-relaxed">
                Most credit-repair companies charge $99/month to do what Aura does automatically —
                and honestly, faster. Here&apos;s the whole loop.
              </p>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-px bg-[#26282C] border border-[#26282C] stagger">
            {steps.map((s) => {
              const Icon = s.icon;
              return (
                <div key={s.title} className="bg-[#0B0C0E] p-8 lg:p-10 lift group">
                  <div className="flex items-start justify-between mb-10">
                    <span className="ticker text-sm text-[#6D716C]">{s.num}</span>
                    <Icon size={22} strokeWidth={1.5} className="text-[#D5FF3F] group-hover:scale-110 transition-transform" />
                  </div>
                  <h3 className="text-2xl mb-3 leading-tight">{s.title}</h3>
                  <p className="text-[#A8ADA5] leading-relaxed text-sm">{s.body}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Features-announce grid — icons + short names only. What's in there without the walk-through. */}
      <section id="stack" className="border-b border-[#26282C]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-28">
          <div className="mb-14 max-w-3xl">
            <div className="overline mb-5">Inside the app</div>
            <h2 className="text-5xl md:text-6xl leading-[0.95] tracking-tight">
              A stack of tools.
              <br />
              <span className="font-display text-[#D5FF3F]">Every one</span> matters.
            </h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-px bg-[#26282C] border border-[#26282C]">
            {featureTiles.map(({ icon: Icon, name }) => (
              <motion.div
                key={name}
                initial={{ opacity: 0, y: 8 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.35 }}
                className="bg-[#0B0C0E] p-6 md:p-7 flex items-center gap-3 group hover:bg-[#131518] transition-colors"
                data-testid={`feature-tile-${name.toLowerCase().replace(/\s+/g, "-")}`}
              >
                <div className="w-10 h-10 rounded-sm bg-[#D5FF3F]/10 border border-[#D5FF3F]/25 flex items-center justify-center group-hover:bg-[#D5FF3F] transition-colors">
                  <Icon size={18} className="text-[#D5FF3F] group-hover:text-[#0B0C0E] transition-colors" strokeWidth={2} />
                </div>
                <span className="text-[#F5F3EE] text-sm md:text-base font-medium">{name}</span>
              </motion.div>
            ))}
          </div>
          <div className="mt-10">
            <Link to="/register" data-testid="perks-cta">
              <Button className="rounded-full btn-lime h-12 px-7">
                See it in action <ArrowUpRight size={16} className="ml-1.5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Pricing — monthly membership on the front. Cancel-anytime framing; details live on /pricing. */}
      <section id="pricing" className="border-b border-[#26282C]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-28">
          <div className="mb-14 max-w-3xl">
            <div className="overline mb-5">Packages</div>
            <h2 className="text-5xl md:text-6xl leading-[0.95] tracking-tight">
              Monthly membership.
              <br />
              <span className="font-display text-[#D5FF3F]">Cancel anytime.</span>
            </h2>
          </div>
          <div className="grid md:grid-cols-2 gap-px bg-[#26282C] border border-[#26282C]">
            <div className="bg-[#0B0C0E] p-8 md:p-10 flex flex-col" data-testid="landing-pkg-basic">
              <div className="flex items-start justify-between mb-6">
                <div>
                  <div className="overline mb-2 text-[#F5F3EE]">Aura Basic</div>
                  <div className="text-[10px] font-mono uppercase tracking-widest text-[#6D716C]">
                    Clean up your credit
                  </div>
                </div>
                <span className="text-[10px] px-2 py-0.5 rounded-full font-mono uppercase tracking-widest bg-[#F5F3EE] text-[#0B0C0E]">
                  Most popular
                </span>
              </div>
              <div className="flex items-baseline gap-2 mb-2">
                <span className="ticker text-7xl text-[#F5F3EE]">
                  ${priceStr(basicPrice) || "29.99"}
                </span>
                <span className="text-[#A8ADA5] font-mono text-base">/mo</span>
              </div>
              <div className="text-[11px] font-mono uppercase tracking-widest text-[#7CFF9B] mb-8">
                Cancel anytime
              </div>
              <Link to="/register" className="mt-auto" data-testid="landing-basic-cta">
                <Button className="w-full rounded-full h-12 btn-outline hover:!bg-[#F5F3EE] hover:!text-[#0B0C0E] hover:!border-[#F5F3EE]">
                  Get Basic <ArrowUpRight size={15} className="ml-1.5" />
                </Button>
              </Link>
            </div>

            <div className="bg-[#0B0C0E] p-8 md:p-10 flex flex-col relative" data-testid="landing-pkg-plus">
              <div className="absolute inset-0 pointer-events-none bg-gradient-to-br from-[#D5FF3F]/6 to-transparent" />
              <div className="relative flex items-start justify-between mb-6">
                <div>
                  <div className="overline mb-2 text-[#D5FF3F]">Aura Plus</div>
                  <div className="text-[10px] font-mono uppercase tracking-widest text-[#6D716C]">
                    Concierge firepower
                  </div>
                </div>
                <span className="text-[10px] px-2 py-0.5 rounded-full font-mono uppercase tracking-widest bg-[#D5FF3F] text-[#0B0C0E]">
                  Everything
                </span>
              </div>
              <div className="relative flex items-baseline gap-2 mb-2">
                <span className="ticker text-7xl text-[#D5FF3F]">
                  ${priceStr(plusPrice) || "49.99"}
                </span>
                <span className="text-[#A8ADA5] font-mono text-base">/mo</span>
              </div>
              <div className="relative text-[11px] font-mono uppercase tracking-widest text-[#D5FF3F] mb-8">
                Cancel anytime
              </div>
              <Link to="/register" className="relative mt-auto" data-testid="landing-plus-cta">
                <Button className="w-full rounded-full h-12 btn-lime">
                  Get Plus <ArrowUpRight size={15} className="ml-1.5" />
                </Button>
              </Link>
            </div>
          </div>
          <p
            className="mt-6 text-[11px] leading-relaxed text-[#6D716C] max-w-3xl"
            data-testid="landing-pricing-fine-print"
          >
            * Each package is a monthly membership billed automatically until you cancel. Cancel any time
            from billing — access continues to the end of your paid period. Certified mail letters are
            billed separately at $12 per letter.
          </p>
        </div>
      </section>

      {/* CTA + disclaimer */}
      <section id="truth" className="relative overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-30" />
        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10 py-32">
          <div className="max-w-4xl">
            <div className="overline mb-6">Real talk</div>
            <h2 className="text-6xl md:text-7xl lg:text-8xl leading-[0.92] tracking-tight mb-10">
              You do the sending.
              <br />
              <span className="font-display text-[#D5FF3F]">We do the</span>
              <br />
              <span className="font-display">thinking.</span>
            </h2>
            <div className="grid md:grid-cols-2 gap-8 text-[#A8ADA5] leading-relaxed mb-14 max-w-3xl">
              <div>
                <div className="text-[#F5F3EE] mb-2 font-medium">Not legal advice.</div>
                <p className="text-sm">
                  Aura&apos;s an educational + productivity tool. Read every letter before you mail it. We
                  don&apos;t guarantee item removal — accuracy of your file is the bureau&apos;s job under the FCRA.
                </p>
              </div>
              <div>
                <div className="text-[#F5F3EE] mb-2 font-medium">CROA-aware.</div>
                <p className="text-sm">
                  You can dispute inaccurate information yourself, for free, at any time. Aura is
                  software that helps you do it well. You stay in the driver&apos;s seat.
                </p>
              </div>
            </div>
            <Link to="/register" data-testid="footer-cta">
              <Button className="rounded-full btn-lime h-14 px-8 text-base">
                Start your first scan — free <ArrowUpRight size={18} className="ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-[#26282C] bg-[#0B0C0E]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-10 flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-2.5">
            <div className="w-6 h-6 rounded-sm bg-[#D5FF3F] flex items-center justify-center">
              <span className="font-display italic text-[#0B0C0E] text-sm leading-none">a</span>
            </div>
            <span className="font-display text-xl">Aura Credit</span>
          </div>
          <div className="flex flex-wrap items-center gap-6 text-xs font-mono uppercase tracking-wider text-[#6D716C]">
            <Link to="/playbook" className="hover:text-[#D5FF3F]">
              Playbook
            </Link>
            <Link to="/terms" className="hover:text-[#D5FF3F]">
              Terms
            </Link>
            <Link to="/privacy" className="hover:text-[#D5FF3F]" data-testid="landing-disclaimer-link">
              Privacy
            </Link>
            <span>© {new Date().getFullYear()} · No human ever reads your file</span>
          </div>
        </div>
      </footer>
    </div>
  );
}
